import React, { useState, useEffect } from 'react';
import { useCms } from '@/context/CmsContext';
import { fetchVersions, restoreVersion, getAccessLoginUrl } from '@/services/cms';
import FilesPanel from './FilesPanel';
import styles from './cms.module.css';

// Configurable trigger param — set via env var or defaults to 'cms'
const CMS_TRIGGER_PARAM = process.env.REACT_APP_CMS_TRIGGER || 'editmode';

// Login lockout settings
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const LOCKOUT_STORAGE_KEY = 'cms_lockout';

function isLockedOut(): boolean {
    const raw = localStorage.getItem(LOCKOUT_STORAGE_KEY);
    if (!raw) return false;
    const { until } = JSON.parse(raw);
    if (Date.now() < until) return true;
    localStorage.removeItem(LOCKOUT_STORAGE_KEY);
    return false;
}

function recordFailedAttempt(): boolean {
    const raw = localStorage.getItem(LOCKOUT_STORAGE_KEY);
    const data = raw ? JSON.parse(raw) : { attempts: 0, until: 0 };
    data.attempts += 1;
    if (data.attempts >= MAX_LOGIN_ATTEMPTS) {
        data.until = Date.now() + LOCKOUT_DURATION_MS;
        localStorage.setItem(LOCKOUT_STORAGE_KEY, JSON.stringify(data));
        return true; // now locked out
    }
    localStorage.setItem(LOCKOUT_STORAGE_KEY, JSON.stringify(data));
    return false;
}

function clearLockout(): void {
    localStorage.removeItem(LOCKOUT_STORAGE_KEY);
}

export default function AdminToolbar() {
    const {
        isAdmin, isEditMode, toggleEditMode,
        saving, saveAll, changeCount, discardChanges,
        undo, redo, canUndo, canRedo,
        login, lastError,
    } = useCms();
    const [showLogin, setShowLogin] = useState(false);
    const [secret, setSecret] = useState('');
    const [error, setError] = useState('');
    const [showSaved, setShowSaved] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [showFiles, setShowFiles] = useState(false);
    const [cmsActive, setCmsActive] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);
    const [lockedOut, setLockedOut] = useState(isLockedOut());

    // Activate CMS via trigger query param or if already authenticated
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        if (params.has(CMS_TRIGGER_PARAM)) {
            // Clean the URL
            const url = new URL(window.location.href);
            url.searchParams.delete(CMS_TRIGGER_PARAM);
            window.history.replaceState({}, '', url.pathname + url.search);

            // Redirect to Cloudflare Access login page
            window.location.href = getAccessLoginUrl();
            return;
        }

        // After CF Access redirect, user lands back with ?cfauth=1
        if (params.has('cfauth')) {
            const url = new URL(window.location.href);
            url.searchParams.delete('cfauth');
            window.history.replaceState({}, '', url.pathname + url.search);
            setCmsActive(true);
            setShowLogin(true);
            return;
        }

        // Returning admin with stored token
        const storedToken = localStorage.getItem('cms_admin_token');
        if (storedToken && !isAdmin) {
            setCmsActive(true);
            login(storedToken).then(ok => {
                if (!ok) {
                    localStorage.removeItem('cms_admin_token');
                    setShowLogin(true);
                }
            });
        }

        if (isAdmin) {
            setCmsActive(true);
        }
    }, [isAdmin]);

    // Warn before closing with unsaved changes
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (changeCount > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [changeCount]);

    // Keyboard shortcuts
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (!isAdmin) return;
            if (e.ctrlKey && e.shiftKey && e.key === 'E') { e.preventDefault(); toggleEditMode(); }
            if (e.ctrlKey && e.key === 's' && isEditMode && changeCount > 0) {
                e.preventDefault();
                handleSave();
            }
            if (e.ctrlKey && !e.shiftKey && e.key === 'z' && isEditMode) { e.preventDefault(); undo(); }
            if (e.ctrlKey && e.shiftKey && e.key === 'Z' && isEditMode) { e.preventDefault(); redo(); }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isAdmin, isEditMode, changeCount, toggleEditMode, saveAll, undo, redo]);

    const handleLogin = async () => {
        if (isLockedOut()) {
            setLockedOut(true);
            setError('Too many failed attempts. Try again later.');
            return;
        }
        setError('');
        const ok = await login(secret);
        if (ok) {
            clearLockout();
            setShowLogin(false);
            setSecret('');
        } else {
            const nowLocked = recordFailedAttempt();
            if (nowLocked) {
                setLockedOut(true);
                setError('Too many failed attempts. Locked out for 15 minutes.');
            } else {
                setError('Invalid secret');
            }
        }
    };

    const handleSave = async () => {
        setSaveFailed(false);
        const ok = await saveAll();
        if (ok) {
            setShowSaved(true);
            setTimeout(() => setShowSaved(false), 2000);
        } else {
            setSaveFailed(true);
            setTimeout(() => setSaveFailed(false), 5000);
        }
    };

    const handleDiscard = () => {
        if (window.confirm(`Discard ${changeCount} unsaved change${changeCount !== 1 ? 's' : ''}?`)) {
            discardChanges();
        }
    };

    // Not active — completely hidden
    if (!cmsActive && !isAdmin) return null;

    // Active but not authenticated — show login modal
    if (!isAdmin) {
        if (!showLogin) return null;
        return (
            <div className={styles.modal} onClick={() => { setShowLogin(false); setCmsActive(false); }}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                    <h3 className={styles.modalTitle}>CMS Admin</h3>
                    <input
                        type="password"
                        placeholder="Admin secret"
                        value={secret}
                        onChange={(e) => setSecret(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                        className={styles.editInput}
                        autoFocus
                    />
                    {error && <div className={styles.loginError}>{error}</div>}
                    <button onClick={handleLogin} disabled={lockedOut} className={styles.toolbarPrimary} style={{ marginTop: 12, width: '100%', opacity: lockedOut ? 0.5 : 1 }}>
                        {lockedOut ? 'Locked' : 'Sign In'}
                    </button>
                </div>
            </div>
        );
    }

    // Authenticated — collapsed state: just a small pill
    if (!isEditMode) {
        return (
            <div className={styles.toolbarContainer}>
                <div className={styles.toolbarCollapsed} onClick={toggleEditMode}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    <span>Edit</span>
                </div>
            </div>
        );
    }

    // Authenticated — expanded toolbar
    return (
        <div className={styles.toolbarContainer}>
            <div className={styles.toolbarBar}>
                {/* Edit toggle */}
                <div className={styles.toolbarSection}>
                    <label className={styles.toggleLabel}>
                        <input type="checkbox" checked={isEditMode} onChange={toggleEditMode} className={styles.toggleInput} />
                        <span className={styles.toggleSwitch} />
                        <span className={styles.toggleText}>Editing</span>
                    </label>
                </div>

                {isEditMode && (
                    <>
                        {/* Undo/redo + change count */}
                        <div className={styles.toolbarDivider} />
                        <div className={styles.toolbarSection}>
                            <button className={styles.toolbarIcon} onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                                </svg>
                            </button>
                            <button className={styles.toolbarIcon} onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" />
                                </svg>
                            </button>

                            {changeCount > 0 && (
                                <span className={styles.changeCount}>
                                    {changeCount} change{changeCount !== 1 ? 's' : ''}
                                </span>
                            )}
                        </div>

                        {/* Discard + History */}
                        <div className={styles.toolbarDivider} />
                        <div className={styles.toolbarSection}>
                            {changeCount > 0 && (
                                <button className={styles.toolbarGhost} onClick={handleDiscard}>
                                    Discard
                                </button>
                            )}
                            <button className={styles.toolbarGhost} onClick={() => { setShowFiles(false); setShowHistory(!showHistory); }}>
                                History
                            </button>
                            <button className={styles.toolbarGhost} onClick={() => { setShowHistory(false); setShowFiles(!showFiles); }}>
                                Files
                            </button>
                        </div>

                        {/* Error message */}
                        {saveFailed && (
                            <span className={styles.toolbarError}>Publish failed — try again</span>
                        )}

                        {/* Publish — far right */}
                        <div className={styles.toolbarSpacer} />
                        <button
                            className={changeCount > 0 ? styles.toolbarPrimary : styles.toolbarPrimaryDisabled}
                            onClick={handleSave}
                            disabled={changeCount === 0 || saving}
                        >
                            {saving ? 'Saving...' : showSaved ? 'Published!' : 'Publish'}
                        </button>
                    </>
                )}
            </div>

            {showHistory && (
                <VersionHistoryPanel
                    onClose={() => setShowHistory(false)}
                    onRestore={async (id) => {
                        await restoreVersion(id);
                        setShowHistory(false);
                        window.location.reload();
                    }}
                />
            )}

            {showFiles && (
                <FilesPanel onClose={() => setShowFiles(false)} />
            )}
        </div>
    );
}

function VersionHistoryPanel({ onClose, onRestore }: { onClose: () => void; onRestore: (id: number) => Promise<void> }) {
    const [versions, setVersions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [restoring, setRestoring] = useState<number | null>(null);
    const [filterText, setFilterText] = useState('');

    // Load recent changes on mount
    useEffect(() => {
        fetchVersions(undefined, undefined, undefined, 30).then(v => {
            setVersions(v);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const handleRestore = async (id: number) => {
        setRestoring(id);
        await onRestore(id);
        setRestoring(null);
    };

    const filtered = filterText
        ? versions.filter(v =>
            `${v.page} ${v.section} ${v.field} ${v.value}`.toLowerCase().includes(filterText.toLowerCase())
        )
        : versions;

    return (
        <div className={styles.historyPanel}>
            <div className={styles.historyHeader}>
                <span className={styles.historyTitle}>Recent Changes</span>
                <button onClick={onClose} className={styles.historyClose}>&times;</button>
            </div>

            <input
                style={{
                    padding: '6px 10px', background: '#1e293b', border: '1px solid #334155',
                    borderRadius: 6, fontSize: 12, fontFamily: 'system-ui', color: '#e2e8f0',
                    width: '100%', boxSizing: 'border-box' as const, outline: 'none', marginBottom: 10,
                }}
                placeholder="Filter changes..."
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                autoFocus
            />

            <div className={styles.historyResults}>
                {loading && <div className={styles.historyEmpty}>Loading...</div>}

                {!loading && filtered.length === 0 && (
                    <div className={styles.historyEmpty}>No changes found.</div>
                )}

                {filtered.map((v: any) => (
                    <div key={v.id} className={styles.historyItem}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div className={styles.historyTimestamp}>
                                {new Date(v.saved_at + 'Z').toLocaleString()}
                                <span style={{ marginLeft: 8, color: '#64748b' }}>
                                    {v.page} &rsaquo; {v.section} &rsaquo; {v.field}
                                </span>
                            </div>
                            <div className={styles.historyValue}>
                                {v.value?.length > 120 ? v.value.slice(0, 120) + '...' : v.value}
                            </div>
                        </div>
                        <button
                            onClick={() => handleRestore(v.id)}
                            disabled={restoring === v.id}
                            className={styles.historyRestoreBtn}
                        >
                            {restoring === v.id ? '...' : 'Restore'}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
